import { useMemo, useCallback, useEffect } from 'react';
import moment from 'moment';

import useDatePicker from './useDatePicker';
import usePersistentPeriod from './usePersistentPeriod';

const usePersistentDatePicker = (opts = {}) => {
  const { exclude = [], queryParam = 'dt' } = opts;

  const [period, setPeriod, getStoredDate, setStoredDateValue] = usePersistentPeriod({ exclude });
  const datePicker = useDatePicker(period, { queryParam });

  const effectiveDate = useMemo(() => {
    const storedDate = getStoredDate();
    if (storedDate && moment.isMoment(storedDate)) {
      const yearAgo = moment().subtract(2, 'years');
      const yearFromNow = moment().add(1, 'year');
      
      if (storedDate.isBetween(yearAgo, yearFromNow)) {
        return storedDate;
      }
    }
    return datePicker.date;
  }, [datePicker.date, getStoredDate]);

  useEffect(() => {
    const storedDate = getStoredDate();
    if (storedDate && moment.isMoment(storedDate)) {
      const yearAgo = moment().subtract(2, 'years');
      const yearFromNow = moment().add(1, 'year');
      
      if (storedDate.isBetween(yearAgo, yearFromNow)) {
        if (!datePicker.date || !datePicker.date.isSame(storedDate, 'day')) {
          datePicker.setDate(storedDate);
        }
      }
    }
  }, [datePicker, getStoredDate]);

  const setDate = useCallback((newDate) => {
    datePicker.setDate(newDate);
    setStoredDateValue(newDate);
  }, [datePicker, setStoredDateValue]);

  const setPeriodAndClearDate = useCallback((newPeriod) => {
    if (newPeriod !== period) {
      setStoredDateValue(null);
    }
    setPeriod(newPeriod);
  }, [setPeriod, setStoredDateValue, period]);

  return {
    mode: datePicker.mode,
    date: effectiveDate,
    setDate,
    period,
    setPeriod: setPeriodAndClearDate,
  };
};

export default usePersistentDatePicker;
